import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { extractOrigins, objectivePassages } from '@/lib/conversation-model';
import { FLOATING_GAP, FLOATING_MARGIN, type FloatingSide } from '@/lib/floating';
import { type ObjectiveTone, objectiveStatusLabel, objectiveTone } from '@/lib/session-labels';
import { useFloatingPlacement } from '@/lib/use-floating-placement';
import type { ProtocolObjective, SessionTurn } from '@matter_extractor/shared';
import { objectiveState } from '@matter_extractor/shared';
import { ArrowRight } from 'lucide-react';
import type { FocusEvent } from 'react';

/**
 * The panel of one objective's matter, opened from its line in the État column
 * (§12.3, issue #172; maquette `conversation-etat.html`).
 *
 * ## The pattern of #104, placed LEFT of its line
 *
 * A floating card in `position: fixed`, never cropped, placed by the same
 * `useFloatingPlacement` every panel of the package calls. The side is the one
 * thing it chooses for itself: the column stands at the right of the screen, so
 * the panel opens on its LEFT, over the transcript - and BELOW the line when
 * there is no room there, which is the phone, where the column runs the whole
 * width. `placeFloating` flips a side to its opposite and never to a
 * perpendicular one, so the fallback to below is decided here, before asking.
 *
 * ## It replaces the bubble of the extract count
 *
 * The count's infobulle said the status word and nothing else; this says the
 * status, and the MATTER the status stands on: the live extracts of the shown
 * snapshot, in full, each with the turn that first carried it - the rule of the
 * Matière tab (`extractOrigins`). The invalidated ones are COUNTED in the
 * subtitle and not shown: they are no longer matter (§8.4), and the Objectifs
 * layer of their turn is where they are read.
 *
 * ## What it owns, and what the column owns
 *
 * It draws and places itself; the column opens it, closes it, and holds the
 * timers and Escape - the panel follows the line under the pointer, and only
 * the owner of every line can make it follow.
 */

export interface ObjectivePanelProps {
  /** The line of the column the panel stands against. */
  anchor: HTMLElement;
  objective: ProtocolObjective;
  /** 1-based rank of the objective's step in the frozen protocol. */
  stepIndex: number;
  turns: readonly SessionTurn[];
  /** 1-based number of the turn the column shows. */
  number: number;
  /** True while nobody has looked at a turn: the column shows the END. */
  atEnd: boolean;
  /** The turn a réussi leads to, or null (`lastPassage`). */
  target: number | null;
  /** Under 768 px nothing hovers, so « aller au tour N » is a link of the panel. */
  narrow: boolean;
  onGoTo: (turn: number) => void;
  onPointerIn: () => void;
  onPointerOut: () => void;
  onFocusOut: (event: FocusEvent<HTMLElement>) => void;
}

/** The maquette's width, needed as a number before the DOM has measured one. */
export const OBJECTIVE_PANEL_WIDTH = 360;

/** The pill of the status, in the four tones of the column's dots. */
const PILL: Readonly<Record<ObjectiveTone, string>> = {
  succeeded: 'bg-vert-tint text-vert-deep',
  passable: 'bg-jaune-tint text-jaune-deep',
  failed: 'bg-rouge-tint text-rouge-deep',
  empty: 'bg-gray-100 text-gray-600',
};

const plural = (count: number, word: string): string => `${count} ${word}${count > 1 ? 's' : ''}`;

/** Left of the line when it fits there, below it otherwise (§12.3). */
function sideFor(anchor: HTMLElement): FloatingSide {
  const room = anchor.getBoundingClientRect().left - FLOATING_MARGIN;
  return room >= OBJECTIVE_PANEL_WIDTH + FLOATING_GAP ? 'left' : 'bottom';
}

export function ObjectivePanel({
  anchor,
  objective,
  stepIndex,
  turns,
  number,
  atEnd,
  target,
  narrow,
  onGoTo,
  onPointerIn,
  onPointerOut,
  onFocusOut,
}: ObjectivePanelProps) {
  const { at, panelRef } = useFloatingPlacement({
    anchor,
    width: OBJECTIVE_PANEL_WIDTH,
    side: sideFor(anchor),
    align: 'start',
  });

  const shown = turns[number - 1];
  const state =
    shown === undefined
      ? { status: 'empty' as const }
      : objectiveState(shown.snapshot, objective.id);
  const tone = objectiveTone(state);
  const extracts = extractOrigins(turns, objective.id, number);
  const live = extracts.filter((extract) => !extract.invalidated);
  const dead = extracts.length - live.length;
  const { passages, losses } = objectivePassages(turns, objective.id, number);
  const when = atEnd ? 'à la fin' : `après le tour ${number}`;

  const subtitle = [
    `Étape ${stepIndex}`,
    objective.mandatory ? 'obligatoire' : 'optionnel',
    when,
    ...(live.length > 0 ? [plural(live.length, 'extrait')] : []),
    ...(dead > 0
      ? [`${dead} invalidé${dead > 1 ? 's' : ''}, non montré${dead > 1 ? 's' : ''}`]
      : []),
  ].join(' · ');

  const firstLoss = losses[0];
  const lastLoss = losses[losses.length - 1];
  const lastWin = passages[passages.length - 1];

  return (
    <div
      ref={panelRef}
      // biome-ignore lint/a11y/useSemanticElements: a `<dialog open>` carries the UA's own absolute placement and margins, which would fight the `fixed` coordinates this panel is placed at; and it is a NON-modal panel, nothing inert behind it - the contract `criterion-card-popover.tsx` holds for the same reason.
      role="dialog"
      aria-label={objective.name}
      data-testid={SESSIONS_TEST_IDS.stateObjectivePanel}
      data-objective-id={objective.id}
      onMouseEnter={onPointerIn}
      onMouseLeave={onPointerOut}
      onBlur={onFocusOut}
      style={{ left: at.left, top: at.top, width: OBJECTIVE_PANEL_WIDTH }}
      className="fixed z-30 flex max-h-[min(440px,calc(100vh-16px))] max-w-[calc(100vw-16px)] flex-col rounded-md border-2 border-gray-200 bg-white text-left shadow-md"
    >
      <header className="flex items-start gap-3 px-4 pt-3.5">
        <h4 className="min-w-0 flex-1 font-semibold text-base text-bleu-nuit">{objective.name}</h4>
        <span
          className={`mt-0.5 inline-flex shrink-0 items-center rounded-pill px-2.5 py-px font-semibold text-xs leading-4 ${PILL[tone]}`}
        >
          {objectiveStatusLabel(tone)}
        </span>
      </header>
      <p className="px-4 pt-1 text-gray-600 text-xs">{subtitle}</p>

      {/*
        Focusable because it SCROLLS - the argument `criterion-card-popover.tsx`
        makes for a direction of 4000 characters: a region a pointer can scroll
        and a keyboard cannot is matter a keyboard reads the top of.
      */}
      <ol
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the rule guards against tab stops that do nothing; this one scrolls.
        tabIndex={0}
        aria-label="Extraits retenus"
        className="mt-2.5 flex min-h-0 flex-col gap-2.5 overflow-y-auto px-4 text-gray-800 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {live.length === 0 ? (
          <li className="text-gray-500 italic">Aucun extrait retenu à ce tour.</li>
        ) : (
          live.map((extract, at) => (
            <li
              // Position in the live list: two live extracts may say the same sentence.
              // biome-ignore lint/suspicious/noArrayIndexKey: the list is rebuilt whole from one snapshot and never reordered.
              key={at}
              className="grid grid-cols-[3.4rem_minmax(0,1fr)] gap-2"
            >
              <span className="whitespace-nowrap pt-px font-semibold text-gray-500 text-xs tabular-nums">
                tour {extract.firstTurn}
              </span>
              <q className="italic">{extract.text}</q>
            </li>
          ))
        )}
      </ol>

      <footer className="mx-4 mt-3 flex flex-col gap-1 border-gray-200 border-t pt-2.5 pb-3.5 text-gray-600 text-xs">
        {target !== null ? (
          <>
            {narrow ? (
              <button
                type="button"
                onClick={() => onGoTo(target)}
                className="inline-flex cursor-pointer items-center gap-1.5 self-start font-semibold text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ArrowRight aria-hidden="true" className="size-3.5" />
                Aller au tour {target}
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-semibold text-bleu-nuit">
                <ArrowRight aria-hidden="true" className="size-3.5" />
                Cliquer la ligne : aller au tour {target}
              </span>
            )}
            {passages.length > 1 && firstLoss !== undefined ? (
              <span>
                Réussi d’abord au tour {passages[0]}, repassé à{' '}
                {objectiveStatusLabel(firstLoss.status)} au tour {firstLoss.turn} : c’est le dernier
                passage à réussi qui compte.
              </span>
            ) : tone === 'passable' ? (
              <span>Validé au plancher à la clôture de l’étape, au tour {target}.</span>
            ) : null}
          </>
        ) : (
          <>
            <span>
              Pas de tour où aller : l’objectif n’est pas réussi {atEnd ? 'à la fin' : 'à ce tour'}.
            </span>
            {lastLoss !== undefined && lastWin !== undefined ? (
              <span>
                Il l’avait été au tour {lastWin}, avant de repasser à{' '}
                {objectiveStatusLabel(lastLoss.status)} au tour {lastLoss.turn}.
              </span>
            ) : null}
          </>
        )}
      </footer>
    </div>
  );
}
